/**
 * EG Search (Meilisearch) – cart helpers.
 *
 * Adds items to cart from search result cards (including variant selection).
 */
window.EGSearchCart = (function () {
  'use strict';

  // Use dynamic references to avoid load-order issues.
  function getAnalytics() { return window.EGSearchAnalytics; }
  function getResultsPage() { return window.EGSearchResultsPage; }

  document.addEventListener('change', (event) => {
    const quantitySelect = event.target.closest?.('.eg-search-qty-select');
    if (!quantitySelect) return;
    const card = quantitySelect.closest('.ais-hit');
    if (!card || card.dataset.isVirtualBundle === 'true') return;
    const quantityInput = card.querySelector('.ais-hit--quantity-input');
    if (quantityInput) quantityInput.value = Math.max(1, parseInt(quantitySelect.value || '1', 10) || 1);
  });

  function isMappedComponentInventoryHandle(handle) {
    const normalizedHandle = String(handle || '').trim();
    if (!normalizedHandle) return false;

    if (typeof window.EGInventoryOverrides?.shouldApplyVirtualBundleOverwrite === 'function') {
      return window.EGInventoryOverrides.shouldApplyVirtualBundleOverwrite(normalizedHandle);
    }

    if (
      normalizedHandle === 'epic-tray-6-cell-germination-dome-and-bottom-tray'
      || normalizedHandle === 'epic-tray-4-cell-germination-dome-and-bottom-tray'
      || normalizedHandle === 'ultimate-beginner-seed-starting-bundle'
      || normalizedHandle === '3-cube-pot'
      || normalizedHandle === 'epic-16-cell-seed-starting-trays'
      || normalizedHandle === '29-tall-birdies-large-4pack'
      || normalizedHandle === '15-short-birdies-large-4pack'
      || normalizedHandle === '29-tall-birdies-medium-4pack'
      || normalizedHandle === '15-short-birdies-medium-4pack'
      || normalizedHandle === '29-tall-birdies-small-4pack'
      || normalizedHandle === '15-short-birdies-small-4pack'
    ) {
      return true;
    }

    const getFixedMappedTargetProductId = window.EGInventoryOverrides?.getFixedMappedTargetProductId;
    if (typeof getFixedMappedTargetProductId === 'function') {
      const mappedId = Number(getFixedMappedTargetProductId(normalizedHandle));
      return Number.isFinite(mappedId) && mappedId > 0;
    }

    return false;
  }

  // Add to cart (respects the currently selected variant)
  window.customSearchAddToCartWithVariant = function (button, event) {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    const card = button.closest('.ais-hit');
    if (card?.dataset.invalidVariantSelection === 'true') return;
    const handle = String(card?.dataset?.handle || '').trim();
    const isVirtualBundle = card?.dataset?.isVirtualBundle === 'true';
    const requiresMappedInventory = isVirtualBundle || isMappedComponentInventoryHandle(handle);
    const mappedOverrideReady = card?.dataset?.useMappedVariant === 'true' && !!card?.dataset?.mappedVariantId;

    if (requiresMappedInventory && !mappedOverrideReady) {
      if (typeof window.EGSearchVariants?.fetchAndRenderVariants === 'function') {
        const fallbackVariantId = card?.dataset?.variantId || button?.dataset?.variantId || '';
        window.EGSearchVariants.fetchAndRenderVariants({
          handle,
          objectID: fallbackVariantId,
          variant_id: fallbackVariantId
        });
      }
      return;
    }

    if (card?.dataset?.useMappedVariant === 'true' && card?.dataset?.mappedVariantAvailable === 'false') {
      button.disabled = true;
      button.setAttribute('aria-disabled', 'true');
      return;
    }

    const variantId = (card?.dataset?.useMappedVariant === 'true' && card?.dataset?.mappedVariantId)
      ? card.dataset.mappedVariantId
      : (card?.dataset?.selectedVariantId || button?.dataset?.variantId);
    const qtySelect = card?.querySelector?.('.eg-search-qty-select');
    const quantity = isVirtualBundle
      ? Math.max(1, parseInt(card?.dataset?.bundleQuantity || '1', 10) || 1)
      : Math.max(1, parseInt(qtySelect?.value || '1', 10) || 1);

    if (!variantId) {
      alert('Please select product options');
      return;
    }

    customSearchAddToCart(variantId, false, handle, event, button, quantity);
  };

  // Add to cart (variant id required)
  window.customSearchAddToCart = async function (variantId, hasVariants, handle, event, buttonEl, quantity) {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    const btn = buttonEl || (event && event.target.closest('.ais-hit--add-to-cart')) || (event && event.target);
    if (!btn || btn.disabled || btn.dataset.egAtcState === 'pending') return;
    const defaultButtonLabel = btn.closest('.ais-hit')?.dataset?.customPreorderCard === 'true'
      ? 'Pre-Order'
      : 'Add to cart';

    let textSpan = btn.querySelector('span:not(.sold-out-message)');
    let loadingSpinner = btn.querySelector('.loading__spinner');

    // console.log('CustomSearch: Add to cart start', { variantId, hasVariants, handle });

    if (hasVariants) {
      window.location.href = `/products/${handle}`;
      return;
    }

    const qty = Math.max(1, parseInt(quantity || 1, 10) || 1);

    // Repair the DOM if we don't have the expected spans/spinner yet.
    if (!textSpan && !loadingSpinner) {
      const childNodes = Array.from(btn.childNodes);
      const textNode = childNodes.find(node => node.nodeType === Node.TEXT_NODE && node.textContent.trim().length > 0);

      if (textNode) {
        const newSpan = document.createElement('span');
        newSpan.textContent = textNode.textContent;
        btn.replaceChild(newSpan, textNode);
        textSpan = newSpan;
      } else {
        textSpan = document.createElement('span');
        textSpan.textContent = defaultButtonLabel;
        btn.appendChild(textSpan);
      }
    }

    if (!loadingSpinner) {
      const spinnerHTML = `
        <div class="loading__spinner hidden">
          <svg aria-hidden="true" focusable="false" class="spinner" viewBox="0 0 66 66" xmlns="http://www.w3.org/2000/svg">
            <circle class="path" fill="none" stroke-width="6" cx="33" cy="33" r="30"></circle>
          </svg>
        </div>`;
      btn.insertAdjacentHTML('beforeend', spinnerHTML);
      loadingSpinner = btn.querySelector('.loading__spinner');
    }

    btn.disabled = true;
    btn.classList.add('loading');
    window.EGAtcFeedback?.pending(btn);

    try {
      const cartDrawer = document.querySelector('cart-drawer');
      let sections = ['cart-drawer', 'cart-icon-bubble'];
      if (cartDrawer && typeof cartDrawer.getSectionsToRender === 'function') {
        sections = cartDrawer.getSectionsToRender().map(s => s.id || s.section);
      }

      // Get the card element from the button
      const card = btn?.closest('.ais-hit');

      const analytics = getAnalytics();
      const resultsPage = getResultsPage();
      const attribution = analytics?.getAttribution?.() || { query: '', source: 'direct', searchId: '', position: 0, origin: window.location.hostname };
      const btnQuery = (btn && btn.dataset && btn.dataset.egQuery) ? String(btn.dataset.egQuery) : '';
      const btnSearchId = (btn && btn.dataset && btn.dataset.egSearchId) ? String(btn.dataset.egSearchId) : '';
      const btnPos = (btn && btn.dataset && btn.dataset.egPosition) ? parseInt(btn.dataset.egPosition || '0', 10) : 0;
      const effectiveQuery = (resultsPage?.getCurrentQuery?.() || btnQuery || attribution.query || '').trim();
      const effectiveSearchId = (resultsPage?.getCurrentSearchId?.() || btnSearchId || attribution.searchId || '').trim();
      const effectivePos = (btnPos || attribution.position || 0);
      const effectiveSource =
        (attribution && attribution.source === 'collection') ? 'collection'
          : (effectiveQuery ? 'search' : (attribution.source || 'direct'));

      // Get product title from the card for better tracking
      const productTitle = card?.querySelector('.ais-hit--title a')?.textContent?.trim() || handle || '';

      // console.log('CustomSearch: ATC tracking data', { card, effectiveQuery, effectiveSource, productTitle });


      // Persist attribution to the cart (so it reaches the order webhook), but do NOT attach it
      // as line-item properties because Shopify checkout will display line-item properties.
      try {
        await fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            attributes: {
              eg_query: effectiveQuery || '',
              eg_source: effectiveSource,
              eg_search_id: effectiveSearchId || '',
              eg_origin_domain: (attribution && attribution.origin) ? attribution.origin : window.location.hostname,
              eg_position: effectivePos || 0,
              eg_ts: String(Date.now()),
            },
          }),
          keepalive: true,
        });
      } catch (e) { /* ignore */ }

      const baseItems = [{ id: Number(variantId), quantity: qty }];
      const preparedItems = window.EGRegiosBedGift?.prepareItems
        ? await window.EGRegiosBedGift.prepareItems(baseItems, { productHandle: handle })
        : baseItems;
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          items: preparedItems,
          sections: sections,
          sections_url: window.location.pathname
        })
      });

      if (!res.ok) {
        const error = await res.json().catch(() => ({}));
        throw new Error(error.description || error.message || 'Unable to add this item. Please try again.');
      }

      const data = await res.json();
      // console.log('CustomSearch: Product added', data);

      // Track conversion
      // console.log('CustomSearch: Tracking ATC', { effectiveQuery, variantId, productTitle, effectiveSource, effectiveSearchId, effectivePos });
      try {
        if (analytics && typeof analytics.trackConversion === 'function') {
          analytics.trackConversion(
            effectiveQuery,
            String(variantId),
            productTitle,
            'add_to_cart',
            'products',
            effectiveSource,
            effectiveSearchId,
            effectivePos
          );
          // console.log('CustomSearch: Conversion tracked successfully');
        } else {
          console.warn('CustomSearch: Analytics not available', { analytics });
        }
      } catch (trackErr) {
        console.warn('CustomSearch: Tracking error', trackErr);
      }

      // Update cart UI
      if (cartDrawer && typeof cartDrawer.renderContents === 'function') {
        cartDrawer.renderContents(data);
      } else {
        const cartIcon = document.getElementById('cart-icon-bubble');
        if (cartIcon) cartIcon.click();
      }

      // Publish update event
      if (window.publish && window.PUB_SUB_EVENTS) {
        window.publish(window.PUB_SUB_EVENTS.cartUpdate, {
          source: 'custom-search',
          cartData: data,
          sections: data.sections
        });
      }

      btn.classList.remove('loading');
      btn.disabled = false;
      window.EGAtcFeedback?.success(btn);
    } catch (err) {
      console.error('Error adding product to cart:', err);
      btn.classList.remove('loading');
      btn.disabled = false;
      window.EGAtcFeedback?.error(btn, err.message);
    }

  };

  return {};
})();
